/**
 * Color specification utilities — converts a hex value to RGB / HSL / CMYK
 * and finds the closest palette-matched pairings (complementary, analogous,
 * triadic) from the library. Every result is mathematically unique per hex,
 * providing genuinely non-templated content for each color page.
 */

export type RGB = { r: number; g: number; b: number };
export type HSL = { h: number; s: number; l: number };
export type CMYK = { c: number; m: number; y: number; k: number };

export function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function hexToHsl(hex: string): HSL {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hexToCmyk(hex: string): CMYK {
  const { r, g, b } = hexToRgb(hex);
  const rp = r / 255, gp = g / 255, bp = b / 255;
  const k = 1 - Math.max(rp, gp, bp);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: Math.round(((1 - rp - k) / (1 - k)) * 100),
    m: Math.round(((1 - gp - k) / (1 - k)) * 100),
    y: Math.round(((1 - bp - k) / (1 - k)) * 100),
    k: Math.round(k * 100),
  };
}

/** Hue distance on the colour wheel (0–180). */
function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Perceptual distance between two hex colours in HSL space. */
function colorDist(hexA: string, hexB: string): number {
  const a = hexToHsl(hexA), b = hexToHsl(hexB);
  const dh = hueDist(a.h, b.h) / 180;
  const ds = Math.abs(a.s - b.s) / 100;
  const dl = Math.abs(a.l - b.l) / 100;
  // Weight hue most heavily; lightness matters but less than hue.
  return Math.sqrt(dh * dh * 2.5 + ds * ds + dl * dl * 1.5);
}

type PairingResult = { id: string; name: string; hex: string; role: string; description: string };

/**
 * Returns 4 palette-matched pairings for a given hex colour:
 * complementary, two analogous neighbours, and one triadic accent.
 * Each is matched to the closest real colour in the library.
 */
export function findPairings(
  sourceHex: string,
  sourceId: string,
  colors: { id: string; name: string; hex: string }[],
): PairingResult[] {
  const { h, s, l } = hexToHsl(sourceHex);
  const candidates = colors.filter((c) => c.id !== sourceId);

  const closest = (targetHue: number, excludeIds: string[]): (typeof candidates)[0] | null => {
    // Build a target hex at the same saturation/lightness but new hue, then
    // find the closest real colour in the library by overall HSL distance.
    const targetHsl = { h: ((targetHue % 360) + 360) % 360, s, l };
    const pool = candidates.filter((c) => !excludeIds.includes(c.id));
    if (!pool.length) return null;

    return pool.reduce<{ color: (typeof pool)[0]; score: number }>(
      (best, c) => {
        const ch = hexToHsl(c.hex);
        const dh = hueDist(ch.h, targetHsl.h) / 180;
        const ds = Math.abs(ch.s - targetHsl.s) / 100;
        const dl = Math.abs(ch.l - targetHsl.l) / 100;
        const score = dh * 2.5 + ds + dl * 0.5; // hue-dominant
        return score < best.score ? { color: c, score } : best;
      },
      { color: pool[0], score: Infinity },
    ).color;
  };

  const used: string[] = [sourceId];
  const results: PairingResult[] = [];

  const comp = closest(h + 180, used);
  if (comp) {
    used.push(comp.id);
    results.push({ ...comp, role: "Complementary", description: `Sits opposite on the colour wheel — creates the strongest contrast and visual tension.` });
  }

  const ana1 = closest(h + 30, used);
  if (ana1) {
    used.push(ana1.id);
    results.push({ ...ana1, role: "Analogous", description: `Adjacent on the wheel — blends harmoniously, sharing tonal warmth or coolness.` });
  }

  const ana2 = closest(h - 30, used);
  if (ana2) {
    used.push(ana2.id);
    results.push({ ...ana2, role: "Analogous", description: `The other adjacent neighbour — reinforces mood without disrupting visual flow.` });
  }

  const tri = closest(h + 120, used);
  if (tri) {
    used.push(tri.id);
    results.push({ ...tri, role: "Triadic accent", description: `One point of a triadic triad — adds vibrancy and variety while keeping balance.` });
  }

  return results;
}

/** Describes the hue angle in plain English (e.g. "blue-green at 165°"). */
export function hueLabel(h: number): string {
  const bands = [
    [0, 15, "red"], [15, 45, "red-orange"], [45, 75, "yellow-orange"],
    [75, 105, "yellow-green"], [105, 150, "green"], [150, 165, "blue-green"],
    [165, 195, "cyan"], [195, 225, "blue-green"], [225, 255, "blue"],
    [255, 285, "blue-violet"], [285, 315, "violet"], [315, 345, "red-violet"],
    [345, 360, "red"],
  ] as [number, number, string][];
  const label = bands.find(([lo, hi]) => h >= lo && h < hi)?.[2] ?? "neutral";
  return `${label} at ${h}°`;
}
